import { useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { MapPin, Plus, Building2, User } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useOutletContext } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";

const redIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});
const blueIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

function ClickPicker({ onPick }) {
  useMapEvents({ click(e) { onPick(e.latlng); } });
  return null;
}

const MAP_CENTER = [39.5, -98.35];

export default function MapPage() {
  const { user } = useAuth();
  const { openAuth } = useOutletContext();
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pickMode, setPickMode] = useState(false);
  const [form, setForm] = useState({ name: "", type: "company", category: "Contractor", address: "", lat: null, lng: null, phone: "", website: "", description: "" });

  const { data: listings = [] } = useQuery({ queryKey: ["listings"], queryFn: async () => (await api.get("/listings")).data });

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const create = useMutation({
    mutationFn: async () => (await api.post("/listings", { ...form, lat: Number(form.lat), lng: Number(form.lng) })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["listings"] }); toast.success("Listing added to the map"); setDialogOpen(false); setForm({ name: "", type: "company", category: "Contractor", address: "", lat: null, lng: null, phone: "", website: "", description: "" }); },
    onError: (e) => toast.error(formatError(e.response?.data?.detail)),
  });

  const startAdd = () => {
    if (!user) { openAuth(); return; }
    setDialogOpen(true);
  };

  return (
    <div className="relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="label-tech text-primary">Network Map</span>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tighter font-black mt-2">Contractor & Supplier Map</h1>
          <p className="text-muted-foreground mt-2">Explore members across the country — or add your own listing.</p>
        </div>
        <Button onClick={startAdd} className="rounded-none bg-primary hover:bg-[#D62828] font-bold" data-testid="add-listing-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Listing
        </Button>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-4 flex gap-4 text-sm">
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-primary inline-block" /> Companies</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-500 inline-block" /> Professionals</span>
      </div>

      <div className="border-y border-border" style={{ height: "70vh" }} data-testid="map-container">
        <MapContainer center={MAP_CENTER} zoom={4} style={{ height: "100%", width: "100%" }}>
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; OpenStreetMap &copy; CARTO'
          />
          {dialogOpen && pickMode && <ClickPicker onPick={(ll) => { setForm((f) => ({ ...f, lat: ll.lat.toFixed(5), lng: ll.lng.toFixed(5) })); setPickMode(false); toast.success("Location selected"); }} />}
          {listings.map((l) => (
            <Marker key={l.id} position={[l.lat, l.lng]} icon={l.type === "professional" ? blueIcon : redIcon}>
              <Popup>
                <div className="min-w-[180px]">
                  <p className="font-bold flex items-center gap-1">{l.type === "professional" ? <User className="w-3 h-3" /> : <Building2 className="w-3 h-3" />} {l.name}</p>
                  <p className="text-xs text-gray-500">{l.category} · {l.address}</p>
                  {l.description && <p className="text-xs mt-1">{l.description}</p>}
                  {l.phone && <p className="text-xs mt-1">📞 {l.phone}</p>}
                  {l.website && <a href={l.website} className="text-xs text-blue-600" target="_blank" rel="noreferrer">Website</a>}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none border-2 border-foreground max-w-md">
          <DialogHeader><DialogTitle className="font-display tracking-tight flex items-center gap-2"><MapPin className="w-5 h-5 text-primary" /> Add Map Listing</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-tech">Name</Label>
              <Input value={form.name} onChange={upd("name")} className="rounded-none mt-1" data-testid="listing-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-tech">Type</Label>
                <select value={form.type} onChange={upd("type")} className="w-full h-10 border border-input px-3 mt-1 rounded-none bg-white" data-testid="listing-type-select">
                  <option value="company">Company</option>
                  <option value="professional">Professional</option>
                </select>
              </div>
              <div>
                <Label className="label-tech">Category</Label>
                <Input value={form.category} onChange={upd("category")} className="rounded-none mt-1" data-testid="listing-category-input" />
              </div>
            </div>
            <div>
              <Label className="label-tech">Address / City</Label>
              <Input value={form.address} onChange={upd("address")} className="rounded-none mt-1" data-testid="listing-address-input" />
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <Label className="label-tech">Latitude</Label>
                <Input value={form.lat ?? ""} onChange={upd("lat")} className="rounded-none mt-1" data-testid="listing-lat-input" />
              </div>
              <div>
                <Label className="label-tech">Longitude</Label>
                <Input value={form.lng ?? ""} onChange={upd("lng")} className="rounded-none mt-1" data-testid="listing-lng-input" />
              </div>
            </div>
            <Button type="button" variant="outline" className="rounded-none w-full border-foreground" onClick={() => { setPickMode(true); toast.info("Click on the map to set the location"); }} data-testid="pick-on-map-btn">
              📍 Pick location on map
            </Button>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-tech">Phone</Label>
                <Input value={form.phone} onChange={upd("phone")} className="rounded-none mt-1" data-testid="listing-phone-input" />
              </div>
              <div>
                <Label className="label-tech">Website</Label>
                <Input value={form.website} onChange={upd("website")} className="rounded-none mt-1" data-testid="listing-website-input" />
              </div>
            </div>
            <div>
              <Label className="label-tech">Description</Label>
              <Textarea value={form.description} onChange={upd("description")} rows={3} className="rounded-none mt-1" data-testid="listing-description-input" />
            </div>
            <Button onClick={() => create.mutate()} disabled={create.isPending || !form.name || !form.lat} className="rounded-none bg-primary hover:bg-[#D62828] font-bold w-full" data-testid="listing-save-btn">
              {create.isPending ? "Saving…" : "Add to Map"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
